/* eslint-disable max-lines-per-function */
/* eslint-disable lines-around-comment */
/* eslint-disable camelcase */
import {ResponseCache, getRuntimeProductPricing} from 'extensions/ui-utils';

/**
 * @typedef {Object} MivaDate
 * @prop {number} time_t
 * @prop {number} year
 * @prop {number} month
 * @prop {number} day
 * @prop {number} hour
 * @prop {number} minute
 * @prop {number} second
 * @prop {number} timezone
 */

/**
 * @typedef {Object} MivaPage
 * @prop {number} id
 * @prop {string} code
 * @prop {string} title
 */

/**
 * @typedef {Object} MivaCategory
 * @prop {number} id
 * @prop {string} code
 * @prop {string} name
 * @prop {string} url
 */

/**
 * @typedef {Object} MivaDiscount
 * @prop {string} descrip
 * @prop {number} discount
 * @prop {string} formatted_discount
 */

/**
 * @typedef {Object} MivaImageSize
 * @prop {string} url
 * @prop {number} height
 * @prop {number} width
 */

/**
 * @typedef {Object} MivaImage
 * @prop {string} code
 * @prop {string} descrip
 * @prop {Record<string, MivaImageSize>} sizes
 */

/**
 * @typedef {Object<string, MivaImageType>} MivaImageTypes
 */

/**
 * @typedef {Object} MivaImageType
 * @prop {string} descrip
 * @prop {Record<string, MivaImageSize>} sizes
 */

/**
 * @typedef {Object} MivaAttributeOption
 * @prop {number} attr_id
 * @prop {number} disp_order
 * @prop {number} id
 * @prop {number} price
 * @prop {number} product_id
 * @prop {number} weight
 * @prop {string} code
 * @prop {string} formatted_price
 * @prop {string} prompt
 */

/**
 * @typedef {Object} MivaAttribute
 * @prop {number} attemp_id
 * @prop {string} code
 * @prop {number} default_id
 * @prop {number} disp_order
 * @prop {string} formatted_price
 * @prop {number} id
 * @prop {string} image
 * @prop {boolean} inventory
 * @prop {MivaAttributeOption[]} options
 * @prop {number} price
 * @prop {number} product_id
 * @prop {string} prompt
 * @prop {boolean} required
 * @prop {string} type
 * @prop {number} weight
 */

/**
 * @typedef {Object} MivaProduct
 * @prop {MivaAttribute} attributes
 * @prop {MivaCategory} category
 * @prop {any} CustomField_Values
 * @prop {MivaDiscount[]} discounts
 * @prop {MivaDate} dt_created
 * @prop {MivaDate} dt_updated
 * @prop {MivaImage[]} images
 * @prop {MivaImageTypes} imagetypes
 * @prop {MivaPage} page
 * @prop {boolean} inv_active
 * @prop {boolean} taxable
 * @prop {number} base_price
 * @prop {number} disp_order
 * @prop {number} id
 * @prop {number} inv_available
 * @prop {number} inv_instock
 * @prop {number} price
 * @prop {number} quantity
 * @prop {number} retail
 * @prop {number} sale_price
 * @prop {number} weight
 * @prop {number} output_disp_order
 * @prop {string} code
 * @prop {string} descrip
 * @prop {string} formatted_base_price
 * @prop {string} formatted_price
 * @prop {string} formatted_retail
 * @prop {string} image
 * @prop {string} inv_level
 * @prop {string} inv_long
 * @prop {string} inv_short
 * @prop {string} name
 * @prop {string} sku
 * @prop {string} thumbnail
 * @prop {string} url
 * @prop {string} inventory_display_html Property is added through the "PRODUCTLIST_LOAD_PRICING" API.mvt function. Contains the HTML from the "inventory_display" content section.
 */

/**
 * @typedef {Object} MivaJsonApiFilterValue
 * @prop {string} field
 * @prop {'CO'|'EQ'|'FALSE'|'GE'|'GT'|'IN'|'LE'|'LIKE'|'LT'|'NC'|'NE'|'NOT_IN'|'NOTLIKE'|'NULL'|'TRUE'} operator
 * @prop {any} value
 */

/**
 * @typedef {Object} MivaJsonApiFilter
 * @prop {string} name
 * @prop {MivaJsonApiFilterValue[]} value
 */

/**
 * @template {any} T
 * @typedef {Object} MivaJsonApiResponse
 * @prop {0|1} success
 * @prop {string} [error_code]
 * @prop {string} [error_message]
 * @prop {T} [data]
 */

/**
 * @typedef {Object} ProductListControllerResponse
 * @prop {number} total_count
 * @prop {number} start_offset
 * @prop {MivaProduct[]} data
 */

/**
 * @typedef {Object} MivaFacetValue
 * @prop {number} count
 * @prop {string} value
 */

/**
 * @typedef {Object} MivaFacet
 * @prop {'checkbox'|'rangeslider'|'radio'} type
 * @prop {MivaFacetValue[]} values
 * @prop {number} [selected_value_high] Only available when type is 'rangeslider'.
 * @prop {number} [selected_value_low] Only available when type is 'rangeslider'.
 * @prop {number} [value_high] Only available when type is 'rangeslider'.
 * @prop {number} [value_low] Only available when type is 'rangeslider'.
 */


/**
 * @typedef {Record<string, MivaFacet>} MivaFacets
 */

export const PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS = {
	cache: false,
	categoryCode: null,
	count: 5,
	endpoint: window.mivaJS.JSON_Url,
	/**
	 * @type {MivaJsonApiFilter[]}
	 */
	filters: [],
	functionName: 'Runtime_ProductList_Load_Query',
	offset: 0,
	onDemandColumns: [],
	sessionId: window.mivaJS.Session_ID,
	sort: 'disp_order',
	storeCode: window.mivaJS.Store_Code
};

/**
 * @type {ResponseCache<MivaJsonApiResponse<ProductListControllerResponse>>}
 */
const cache = new ResponseCache();

export class ProductListController {

	/**
	 * @type {MivaFacets}
	 */
	facets = {};

	/**
	 * @type {import('lit').ReactiveControllerHost}
	 */
	host;

	/**
	 * @type {MivaProduct[]}
	 */
	products = [];

	/**
	 * @type {ProductListControllerResponse}
	 */
	response;

	/**
	 * @type {boolean}
	 */
	loaded = false;

	constructor (host, options = PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS) {
		if (host) {
			// Add controller to host
			(this.host = host).addController(this);
		}

		// Set options
		this.cache = options.cache ?? PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS.cache;
		this.categoryCode = options.categoryCode ?? PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS.categoryCode;
		this.count = options.count ?? PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS.count;
		this.endpoint = options.endpoint ?? PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS.endpoint;
		this.filters = options.filters ?? PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS.filters;
		this.functionName = options.functionName ?? PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS.functionName;
		this.offset = options.offset ?? PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS.offset;
		this.onDemandColumns = options.onDemandColumns ?? PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS.onDemandColumns;
		this.sessionId = options.sessionId ?? PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS.sessionId;
		this.sort = options.sort ?? PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS.sort;
		this.storeCode = options.storeCode ?? PRODUCT_LIST_CONTROLLER_DEFAULT_OPTIONS.storeCode;
	}

	get body () {
		const body = {
			count: this.count,
			filter: [
				...this.filters,
				{
					name: 'ondemandcolumns',
					value: this.onDemandColumns
				}
			],
			function: this.functionName,
			offset: this.offset,
			session_id: this.sessionId,
			session_type: 'runtime',
			sort: this.sort,
			store_code: this.storeCode
		};

		if (this.categoryCode) {
			body.Category_Code = this.categoryCode;
		}

		return JSON.stringify(body);
	}

	async #fetch (body) {
		// Get the response
		const response = await fetch(this.endpoint, {
			body,
			headers: {
				'Content-Type': 'application/json'
			},
			method: 'POST'
		});

		const json = await response.json();

		if (this.cache) {
			cache.set(body, json);
		}

		return json;
	}

	async #getResponse () {
		if (this.loaded) {
			return Promise.resolve();
		}

		// Set loaded to false
		this.loaded = false;

		/**
		 * @type {MivaJsonApiResponse<ProductListControllerResponse>}
		 */
		let mivaResponse = {};

		if (this.cache) {
			const found = cache.get(this.body);

			mivaResponse = found ?? await this.#fetch(this.body);
		}
		else {
			mivaResponse = await this.#fetch();
		}

		// Handle response success or error
		if (mivaResponse.success) {
			// Set response
			this.response = mivaResponse.data;

			// Set products array
			this.products = this.response.data;

			// Set facets object
			this.facets = this.response.facets;
		}
		else {
			// eslint-disable-next-line no-console
			console.error(this.constructor.name, '::', `${mivaResponse.error_code}: ${mivaResponse.error_message}`);

			// Unset products array and response
			this.products = [];
			this.response = {};
			this.facets = {};
		}

		// Set loaded to true
		this.loaded = true;

		// Request an update to the host
		this.host?.requestUpdate();

		return void 0;
	}

	/**
	 * Send the Runtime API call and return products
	 * @async
	 * @prop {boolean} runtimePricing
	 */
	async getProducts (runtimePricing = true) {
		await this.#getResponse();

		// Get correct pricing from API page
		if (runtimePricing) {
			this.products = await getRuntimeProductPricing(this.products, this.cache);
		}

		// Return the product list
		return this.products;
	}

	/**
	 * Send the Runtime API call and return facets
	 * @async
	 */
	async getFacets () {
		await this.#getResponse();

		// Return the product list
		return this.facets;
	}

	/**
	 * Send the Runtime API call and return both products and facets
	 * @async
	 * @prop {boolean} runtimePricing
	 */
	async getResponse (runtimePricing = true) {
		await this.#getResponse();

		// Get correct pricing from API page
		if (runtimePricing) {
			this.products = await getRuntimeProductPricing(this.products, this.cache);
		}

		// Return the product list
		return {
			facets: this.facets,
			products: this.products
		};
	}

};
